import { CampaignContactActions } from '../components/landing/CampaignContactActions';
import { CampaignLandingLayout } from '../components/landing/CampaignLandingLayout';
import { MedicalNegligenceLeadForm } from '../components/landing/MedicalNegligenceLeadForm';
import { Icon } from '../components/Icon';
import { Reveal } from '../components/Reveal';
import { CONTACT, buildWhatsAppUrl } from '../data/site';
import {
  MEDICAL_NEGLIGENCE_LANDING as LANDING,
  MEDICAL_NEGLIGENCE_TESTIMONIALS,
  MEDICAL_NEGLIGENCE_WHATSAPP_MESSAGE,
} from '../data/medicalNegligenceLanding';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import '../styles/medical-negligence-landing.css';

export function MedicalNegligenceLandingPage() {
  useDocumentMeta({
    title: 'Negligencia médica | Corporación Indemniza',
    description:
      'Evaluamos posibles casos de negligencia médica, con especial atención a clínicas privadas y médicos particulares. Soporte legal y técnico pericial.',
    canonicalPath: '/negligencia-medica',
    image: '/og-negligencia-medica.webp',
    imageAlt: 'Conversación sobre un posible caso de negligencia médica',
  });

  const whatsappUrl = buildWhatsAppUrl(MEDICAL_NEGLIGENCE_WHATSAPP_MESSAGE);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT.address)}`;

  return (
    <CampaignLandingLayout>
      <div className="medical-landing">
        <section className="medical-hero" aria-labelledby="medical-hero-title">
          <div className="site-container medical-hero__grid">
            <Reveal className="medical-hero__copy" direction="left">
              <p className="eyebrow">{LANDING.hero.eyebrow}</p>
              <h1 id="medical-hero-title">{LANDING.hero.title}</h1>
              <p className="medical-hero__focus">{LANDING.hero.focus}</p>
              <p className="medical-hero__description">{LANDING.hero.description}</p>

              <a
                className="medical-hero__phone-number"
                href={`tel:+${CONTACT.phoneInternational}`}
                data-analytics-event="hero_phone"
              >
                <span><Icon name="phone" size={20} /> Llámanos</span>
                <strong>{CONTACT.phoneDisplay}</strong>
              </a>

              <CampaignContactActions analyticsPrefix="hero" />

              <p className="medical-hero__microcopy">
                Contarnos inicialmente tu caso y recibir una revisión preliminar rápida no tiene costo. Si se requiere una reunión, evaluación detallada u otro servicio profesional, te informaremos previamente.
              </p>
            </Reveal>

            <Reveal className="medical-hero__visual" direction="right" delay={80}>
              <img
                src={LANDING.hero.image}
                srcSet={LANDING.hero.imageSrcSet}
                sizes={LANDING.hero.imageSizes}
                width={LANDING.hero.imageWidth}
                height={LANDING.hero.imageHeight}
                alt="Profesional revisando una radiografía durante una conversación sobre un caso médico"
                fetchPriority="high"
              />
              <div className="medical-hero__visual-note">
                <Icon name="document" size={20} />
                <span>Equipo legal + soporte técnico pericial</span>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="medical-trust" aria-label="Experiencia y especialización">
          <div className="site-container medical-trust__grid">
            {LANDING.trustItems.map((item, index) => (
              <Reveal className="medical-trust__item" delay={index * 60} key={item.title}>
                <span className="medical-icon"><Icon name={item.icon} size={21} /></span>
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <section className="medical-private-care" aria-labelledby="medical-private-care-title">
          <div className="site-container medical-private-care__layout">
            <Reveal as="header" className="medical-private-care__intro" direction="left">
              <p className="eyebrow">{LANDING.privateCare.eyebrow}</p>
              <h2 id="medical-private-care-title">{LANDING.privateCare.title}</h2>
              <p>{LANDING.privateCare.description}</p>
              <p className="medical-private-care__note">{LANDING.privateCare.note}</p>
            </Reveal>

            <div className="medical-private-care__grid">
              {LANDING.privateCare.items.map((item, index) => (
                <Reveal as="article" className="medical-private-care__card" delay={index * 55} key={item.title}>
                  <span className="medical-icon"><Icon name={item.icon} size={20} /></span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="medical-flexibility" aria-labelledby="medical-flexibility-title">
          <div className="site-container">
            <Reveal className="medical-flexibility__card">
              <span className="medical-flexibility__icon" aria-hidden="true">
                <Icon name="handshake" size={26} />
              </span>
              <div className="medical-flexibility__content">
                <p className="eyebrow">Alternativas de trabajo</p>
                <h2 id="medical-flexibility-title">{LANDING.pricingMessage}</h2>
                <p>
                  Cada situación es distinta. Luego de conocer tu caso, te explicamos las alternativas de trabajo que correspondan.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="medical-section medical-response" aria-labelledby="medical-response-title">
          <div className="site-container medical-response__grid">
            <Reveal className="medical-response__visual" direction="left">
              <div className="medical-response__image-frame" aria-hidden="true">
                <img src={LANDING.response.image} alt="" loading="lazy" />
              </div>
            </Reveal>

            <Reveal className="medical-response__content" direction="right" delay={70}>
              <p className="eyebrow">{LANDING.response.eyebrow}</p>
              <h2 id="medical-response-title">
                {LANDING.response.title}
                <span>{LANDING.response.emphasis}</span>
              </h2>
              <p>{LANDING.response.description}</p>

              <a
                className="medical-response__phone"
                href={`tel:+${CONTACT.phoneInternational}`}
                data-analytics-event="response_phone"
              >
                <Icon name="phone" size={23} />
                <strong>{CONTACT.phoneDisplay}</strong>
              </a>

              <CampaignContactActions analyticsPrefix="response" theme="light" />
            </Reveal>
          </div>
        </section>

        <section className="medical-section medical-section--white medical-team" aria-labelledby="medical-team-title">
          <div className="site-container medical-team__layout">
            <Reveal className="medical-team__visual" direction="left">
              <img
                className="medical-team__main-image"
                src={LANDING.team.image}
                srcSet={LANDING.team.imageSrcSet}
                sizes={LANDING.team.imageSizes}
                width={LANDING.team.imageWidth}
                height={LANDING.team.imageHeight}
                alt="Profesionales reunidos en un entorno de trabajo"
                loading="lazy"
                decoding="async"
              />
              <div className="medical-team__leader-card">
                <img
                  src={LANDING.founder.image}
                  width={LANDING.founder.imageWidth}
                  height={LANDING.founder.imageHeight}
                  alt={`${LANDING.founder.name}, ${LANDING.founder.role} de Corporación Indemniza`}
                  loading="lazy"
                  decoding="async"
                />
                <div>
                  <small>Equipo liderado por</small>
                  <strong>{LANDING.founder.name}</strong>
                  <span>{LANDING.founder.role} · {LANDING.founder.area}</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="medical-team__content" direction="right" delay={70}>
              <p className="eyebrow">Corporación Indemniza</p>
              <h2 id="medical-team-title">{LANDING.team.title}</h2>
              <p className="medical-team__intro">{LANDING.team.description}</p>

              <div className="medical-team__capabilities">
                {LANDING.team.capabilities.map((item) => (
                  <article className="medical-team__capability" key={item.title}>
                    <span className="medical-icon"><Icon name={item.icon} size={20} /></span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                    </div>
                  </article>
                ))}
              </div>

              <div className="medical-team__experience">
                <Icon name="hourglass" size={22} />
                <div>
                  <strong>Más de 25 años de experiencia</strong>
                  <span>en materia indemnizatoria</span>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="medical-section medical-section--burgundy" aria-labelledby="medical-process-title">
          <div className="site-container medical-process-layout">
            <Reveal as="header" className="medical-section__heading medical-section__heading--light" direction="left">
              <p className="eyebrow">Así empieza</p>
              <h2 id="medical-process-title">Primero cuéntanos qué pasó</h2>
              <p>
                El primer contacto sirve para entender la situación. Puedes escribirnos, enviarnos un audio o compartir un breve documento por WhatsApp; no necesitas preparar una explicación perfecta para empezar.
              </p>
            </Reveal>

            <div className="medical-process-list">
              {LANDING.process.map((item, index) => (
                <Reveal as="article" className="medical-process-step" direction="right" delay={index * 65} key={item.step}>
                  <span>{item.step}</span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {MEDICAL_NEGLIGENCE_TESTIMONIALS.length > 0 ? (
          <section className="medical-section medical-testimonials" aria-labelledby="medical-testimonials-title">
            <div className="site-container">
              <Reveal as="header" className="medical-section__heading medical-section__heading--center">
                <p className="eyebrow">Experiencias reales</p>
                <h2 id="medical-testimonials-title">Testimonios de nuestros clientes</h2>
                <p>Opiniones publicadas únicamente con autorización de quienes confiaron en Corporación Indemniza.</p>
              </Reveal>

              <div className="medical-testimonials__grid">
                {MEDICAL_NEGLIGENCE_TESTIMONIALS.map((testimonial) => (
                  <Reveal as="figure" className="medical-testimonial" key={`${testimonial.author}-${testimonial.quote}`}>
                    <span className="medical-testimonial__mark" aria-hidden="true">“</span>
                    <blockquote>{testimonial.quote}</blockquote>
                    <figcaption>
                      <strong>{testimonial.author}</strong>
                      {testimonial.detail ? <span>{testimonial.detail}</span> : null}
                    </figcaption>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        ) : null}

        <section className="medical-section medical-office" aria-labelledby="medical-office-title">
          <div className="site-container">
            <Reveal as="header" className="medical-section__heading medical-section__heading--center">
              <p className="eyebrow">Presencia y respaldo</p>
              <h2 id="medical-office-title">{LANDING.office.title}</h2>
              <p>{LANDING.office.description}</p>
            </Reveal>

            <div className="medical-office__grid">
              <Reveal className="medical-office__building" direction="left">
                <img
                  src={LANDING.office.image}
                  srcSet={LANDING.office.imageSrcSet}
                  sizes={LANDING.office.imageSizes}
                  width={LANDING.office.imageWidth}
                  height={LANDING.office.imageHeight}
                  alt="Edificio donde se ubica Corporación Indemniza en Santiago de Surco"
                  loading="lazy"
                  decoding="async"
                />
                <div className="medical-office__address-card">
                  <span className="medical-icon"><Icon name="mapPin" size={22} /></span>
                  <div>
                    <small>Oficina</small>
                    <address>{CONTACT.address}</address>
                  </div>
                </div>
              </Reveal>

              <Reveal className="medical-office__map-card" direction="right" delay={70}>
                <iframe
                  title="Ubicación de Corporación Indemniza"
                  src={CONTACT.mapEmbedUrl}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
                <div className="medical-office__map-actions">
                  <a
                    className="campaign-button campaign-button--gold"
                    href={mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-analytics-event="maps_click"
                  >
                    <Icon name="mapPin" size={19} />
                    Cómo llegar
                  </a>
                  <a
                    className="campaign-button campaign-button--outline-dark medical-office__website-link"
                    href={CONTACT.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-analytics-event="institutional_site_click"
                  >
                    <Icon name="building" size={19} />
                    <span>
                      <small>Web institucional</small>
                      www.indemniza.pe
                    </span>
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="medical-section medical-section--contact" aria-labelledby="medical-lead-form-title">
          <div className="site-container medical-contact-grid">
            <Reveal direction="left">
              <MedicalNegligenceLeadForm />
            </Reveal>

            <Reveal className="medical-contact-panel" direction="right" delay={80}>
              <p className="eyebrow">Contacto directo</p>
              <h2>¿Prefieres conversar primero?</h2>
              <p>La llamada es directa. Por WhatsApp puedes contarnos lo ocurrido con un mensaje de texto, un audio o un breve documento.</p>

              <a
                className="medical-contact-panel__phone-primary"
                href={`tel:+${CONTACT.phoneInternational}`}
                data-analytics-event="contact_phone"
              >
                <span>Llámanos</span>
                <strong>{CONTACT.phoneDisplay}</strong>
              </a>

              <div className="medical-contact-panel__links">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-analytics-event="contact_whatsapp">
                  <span className="medical-icon"><Icon name="whatsapp" size={20} /></span>
                  <span><small>WhatsApp</small><strong>Cuéntanos tu caso</strong></span>
                </a>
              </div>

              <div className="medical-contact-panel__pricing">
                <Icon name="handshake" size={21} />
                <p>{LANDING.pricingMessage}</p>
              </div>

              <div className="medical-contact-panel__details">
                <p><Icon name="clock" size={17} /> {CONTACT.hours}</p>
                <p><Icon name="mapPin" size={17} /> {CONTACT.shortAddress}</p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="medical-section medical-section--cream medical-faq" aria-labelledby="medical-faq-title">
          <div className="site-container medical-faq__layout">
            <Reveal as="header" className="medical-section__heading" direction="left">
              <p className="eyebrow">Preguntas frecuentes</p>
              <h2 id="medical-faq-title">Antes de contarnos tu caso</h2>
              <p>Información clara sobre el primer contacto y qué puedes esperar después.</p>
            </Reveal>

            <Reveal className="medical-faq__list" direction="right" delay={70}>
              {LANDING.faqs.map((faq) => (
                <details className="medical-faq__item" key={faq.question}>
                  <summary>
                    <span>{faq.question}</span>
                    <Icon name="arrowRight" size={18} />
                  </summary>
                  <p>{faq.answer}</p>
                </details>
              ))}
            </Reveal>
          </div>
        </section>

        <section className="medical-final-cta" aria-labelledby="medical-final-cta-title">
          <div className="site-container">
            <Reveal className="medical-final-cta__content">
              <p className="eyebrow">Hablemos</p>
              <h2 id="medical-final-cta-title">Cuéntanos tu caso o llámanos directamente</h2>
              <a
                className="medical-final-cta__phone"
                href={`tel:+${CONTACT.phoneInternational}`}
                data-analytics-event="final_phone"
              >
                {CONTACT.phoneDisplay}
              </a>
              <p>Por WhatsApp puedes enviarnos un mensaje, un audio o un breve documento contándonos lo ocurrido.</p>
              <div className="medical-final-cta__pricing">
                <Icon name="handshake" size={21} />
                <strong>{LANDING.pricingMessage}</strong>
              </div>
              <CampaignContactActions analyticsPrefix="final" className="medical-final-cta__actions" />
            </Reveal>
          </div>
        </section>
      </div>
    </CampaignLandingLayout>
  );
}
